import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  extractAuthorizationCode,
  exchangeCodeForTokens,
  getBusinessLocations
} from '../../services/googleOAuth';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../../components/ui/card';
import LocationSelector from '../../components/LocationSelector/LocationSelector';

const GoogleCallback = () => {
  const history = useHistory();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing authentication...');
  const [clientId, setClientId] = useState(null);
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [openerOrigin, setOpenerOrigin] = useState(null);

  const notifyOnboardingOpener = (success, data, targetOrigin) => {
    if (!window.opener || window.opener.closed) return;
    const messageOrigin =
      targetOrigin === window.location.origin
        ? targetOrigin
        : window.location.origin;

    window.opener.postMessage(
      {
        type: 'GOOGLE_ONBOARDING_OAUTH_RESULT',
        success,
        ...data
      },
      messageOrigin
    );
    window.setTimeout(() => window.close(), 150);
  };

  const notifyOAuthSuccessAndClose = (location) => {
    const payload = {
      type: 'GOOGLE_OAUTH_SUCCESS',
      networkName: 'Google Business',
      clientId,
      location
    };

    if (window.opener && !window.opener.closed) {
      try {
        // Shared fallback channel between popup and opener.
        window.opener.localStorage.setItem(
          'oauth_social_connect_success_ls',
          JSON.stringify({
            networkName: 'Google Business',
            clientId,
            ts: Date.now()
          })
        );
      } catch (_) {}

      try {
        window.opener.postMessage(payload, openerOrigin || '*');
      } catch (_) {
        try {
          window.opener.postMessage(payload, '*');
        } catch (_) {}
      }

      setTimeout(() => {
        window.close();
      }, 150);
      return;
    }

    // Redirigir de vuelta a la página del cliente
    history.push(`/client/${clientId}`);
  };

  useEffect(() => {
    const handleCallback = async () => {
      let stateData = {};
      try {
        // Extrae el código de autorización de la URL
        const { code, state, error, errorDescription } =
          extractAuthorizationCode();
        stateData = state ? JSON.parse(decodeURIComponent(state)) : {};
        const {
          clientId: oauthClientId,
          openerOrigin: oauthOpenerOrigin,
          onboarding
        } = stateData;

        setClientId(oauthClientId);
        setOpenerOrigin(oauthOpenerOrigin || null);

        if (error) {
          setStatus('error');
          setMessage(`Authentication failed: ${errorDescription || error}`);
          if (onboarding) {
            notifyOnboardingOpener(
              false,
              { error: errorDescription || error },
              oauthOpenerOrigin
            );
          }
          return;
        }

        if (!code) {
          setStatus('error');
          setMessage('No authorization code received');
          if (onboarding) {
            notifyOnboardingOpener(
              false,
              { error: 'No authorization code received from Google.' },
              oauthOpenerOrigin
            );
          }
          return;
        }

        setMessage('Exchanging authorization code for tokens...');

        // Intercambia el código por tokens (llamada al backend)
        const result = await exchangeCodeForTokens(
          code,
          process.env.REACT_APP_API_OAUTH_EXCHANGE,
          oauthClientId
        );

        if (result.success && onboarding) {
          const locationsResult = await getBusinessLocations(
            process.env.REACT_APP_API_OAUTH_GET_LOCATIONS,
            oauthClientId
          );
          if (
            !locationsResult.success ||
            !Array.isArray(locationsResult.locations)
          ) {
            throw new Error(
              locationsResult.error || 'Google locations could not be loaded.'
            );
          }

          setStatus('success');
          setMessage('Google Business Profile connected successfully!');
          notifyOnboardingOpener(
            true,
            { locations: locationsResult.locations },
            oauthOpenerOrigin
          );
        } else if (result.success) {
          setStatus('success');
          setMessage('Google Business Profile connected successfully!');

          // Mostrar el selector de ubicaciones después de 1 segundo
          setTimeout(() => {
            setShowLocationSelector(true);
          }, 1000);
        } else {
          setStatus('error');
          setMessage(
            result.error || 'Failed to connect Google Business Profile'
          );
          if (onboarding) {
            notifyOnboardingOpener(
              false,
              {
                error:
                  result.error || 'Failed to connect Google Business Profile'
              },
              oauthOpenerOrigin
            );
          }
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setMessage(error.message || 'An error occurred during authentication');
        if (stateData.onboarding) {
          notifyOnboardingOpener(
            false,
            {
              error: error.message || 'An error occurred during authentication.'
            },
            stateData.openerOrigin
          );
        }
      }
    };

    handleCallback();
  }, [history]);

  const handleLocationSelected = (location) => {
    notifyOAuthSuccessAndClose(location);
  };

  const handleSkipLocationSelection = () => {
    notifyOAuthSuccessAndClose();
  };

  if (showLocationSelector) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <LocationSelector
          clientId={clientId}
          onComplete={handleLocationSelected}
          onSkip={handleSkipLocationSelection}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>
            {status === 'processing' && 'Connecting Google Business...'}
            {status === 'success' && 'Success!'}
            {status === 'error' && 'Connection Failed'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center gap-4">
            {status === 'processing' && (
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            )}
            {status === 'success' && (
              <div className="text-green-600 text-5xl">✓</div>
            )}
            {status === 'error' && (
              <div className="text-red-600 text-5xl">✗</div>
            )}
            <p className="text-center text-sm text-gray-600">{message}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GoogleCallback;
